import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/atoms/Button';
import { Input, Upload, Select, Checkbox } from 'antd';
import type { UploadFile } from 'antd';
import toast from 'react-hot-toast';
import { clinicSetupService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;

interface ClinicSetupFormData {
  // Documents
  ktaPdhiDocument: UploadFile[];
  ktpDocument: UploadFile[];

  // Clinic Info
  clinicName: string;
  businessType: 'pt' | 'cv' | 'perseorangan' | '';
  address: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
  email: string;

  // Facilities
  facilities: {
    diagnostic: string[];
    laboratory: string[];
    hospitalization: string[];
    surgery: string[];
  };
}

const ClinicSetup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState<ClinicSetupFormData>({
    ktaPdhiDocument: [],
    ktpDocument: [],
    clinicName: '',
    businessType: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    phone: '',
    email: '',
    facilities: {
      diagnostic: [],
      laboratory: [],
      hospitalization: [],
      surgery: [],
    },
  });

  // Pre-fill email from user session
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [user]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Facility options
  const diagnosticOptions = [
    { value: 'xray', label: t('clinicSetup.facilities.diagnostic.xray') },
    { value: 'usg', label: t('clinicSetup.facilities.diagnostic.usg') },
    { value: 'ekg', label: t('clinicSetup.facilities.diagnostic.ekg') },
  ];

  const laboratoryOptions = [
    { value: 'hematology', label: t('clinicSetup.facilities.laboratory.hematology') },
    { value: 'bloodChemistry', label: t('clinicSetup.facilities.laboratory.bloodChemistry') },
    { value: 'bloodSmear', label: t('clinicSetup.facilities.laboratory.bloodSmear') },
    { value: 'pcr', label: t('clinicSetup.facilities.laboratory.pcr') },
  ];

  const hospitalizationOptions = [
    { value: 'catNonInfectious', label: t('clinicSetup.facilities.hospitalization.catNonInfectious') },
    { value: 'catRespiratoryInfectious', label: t('clinicSetup.facilities.hospitalization.catRespiratoryInfectious') },
    { value: 'catDigestiveInfectious', label: t('clinicSetup.facilities.hospitalization.catDigestiveInfectious') },
    { value: 'dogNonInfectious', label: t('clinicSetup.facilities.hospitalization.dogNonInfectious') },
    { value: 'dogRespiratoryInfectious', label: t('clinicSetup.facilities.hospitalization.dogRespiratoryInfectious') },
    { value: 'dogDigestiveInfectious', label: t('clinicSetup.facilities.hospitalization.dogDigestiveInfectious') },
  ];

  const surgeryOptions = [
    { value: 'minor', label: t('clinicSetup.facilities.surgery.minor') },
    { value: 'softTissue', label: t('clinicSetup.facilities.surgery.softTissue') },
    { value: 'orthopedic', label: t('clinicSetup.facilities.surgery.orthopedic') },
  ];

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.ktaPdhiDocument.length === 0) {
      newErrors.ktaPdhiDocument = t('clinicSetup.validation.ktaPdhiRequired');
    }

    if (formData.ktpDocument.length === 0) {
      newErrors.ktpDocument = t('clinicSetup.validation.ktpRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = t('clinicSetup.validation.clinicNameRequired');
    }

    if (!formData.businessType) {
      newErrors.businessType = t('clinicSetup.validation.businessTypeRequired');
    }

    if (!formData.address.trim()) {
      newErrors.address = t('clinicSetup.validation.addressRequired');
    }

    if (!formData.city.trim()) {
      newErrors.city = t('clinicSetup.validation.cityRequired');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('clinicSetup.validation.phoneRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('clinicSetup.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('clinicSetup.validation.emailFormat');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      setErrors({});
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Step 1: Upload KTP document
      if (formData.ktpDocument.length === 0) {
        toast.error(t('clinicSetup.validation.ktpRequired'));
        setIsLoading(false);
        return;
      }

      const ktpFile = formData.ktpDocument[0].originFileObj as File;
      const ktpResponse = await clinicSetupService.uploadKTP(ktpFile);

      if (!ktpResponse.success || !ktpResponse.data) {
        toast.error(t('clinicSetup.error.uploadKtp'));
        setIsLoading(false);
        return;
      }

      // Step 2: Upload KTA PDHI document
      if (formData.ktaPdhiDocument.length === 0) {
        toast.error(t('clinicSetup.validation.ktaPdhiRequired'));
        setIsLoading(false);
        return;
      }

      const ktaPdhiFile = formData.ktaPdhiDocument[0].originFileObj as File;
      const ktaPdhiResponse = await clinicSetupService.uploadKTAPDHI(ktaPdhiFile);

      if (!ktaPdhiResponse.success || !ktaPdhiResponse.data) {
        toast.error(t('clinicSetup.error.uploadKtaPdhi'));
        setIsLoading(false);
        return;
      }

      // Step 3: Submit clinic setup data
      const setupData = {
        ktpDocumentUrl: ktpResponse.data.url,
        ktaPdhiDocumentUrl: ktaPdhiResponse.data.url,
        clinicName: formData.clinicName,
        businessType: formData.businessType as 'pt' | 'cv' | 'perseorangan',
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode,
        phone: formData.phone,
        email: formData.email,
        facilities: formData.facilities,
      };

      const response = await clinicSetupService.setupClinic(setupData);

      if (response.success) {
        toast.success(t('clinicSetup.success'));
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(response.message || t('clinicSetup.error.general'));
      }
    } catch (error: any) {
      console.error('Failed to setup clinic:', error);
      const errorMessage = error.response?.data?.message || error.message || t('clinicSetup.error.general');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFacilityChange = (category: keyof ClinicSetupFormData['facilities'], values: string[]) => {
    setFormData(prev => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [category]: values,
      },
    }));
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 py-12">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t('clinicSetup.title')}
          </h1>
          <p className="mt-3 text-lg text-gray-600">
            {t('clinicSetup.subtitle')}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border-2 text-sm font-bold transition-all ${
                      currentStep >= step
                        ? 'border-indigo-600 bg-indigo-600 text-white'
                        : 'border-gray-300 bg-white text-gray-400'
                    }`}
                  >
                    {step}
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-600">
                    {t(`clinicSetup.steps.step${step}`)}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`mx-4 h-1 w-24 transition-all ${
                      currentStep > step ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
          {/* Step 1: Documents */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">{t('clinicSetup.documentsTitle')}</h2>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.ktaPdhiLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Upload
                    listType="picture-card"
                    fileList={formData.ktaPdhiDocument}
                    onChange={({ fileList }) => handleInputChange('ktaPdhiDocument', fileList)}
                    beforeUpload={() => false}
                    maxCount={1}
                  >
                    {formData.ktaPdhiDocument.length === 0 && (
                      <div>
                        <div className="text-2xl">📄</div>
                        <div className="mt-2 text-xs">{t('clinicSetup.uploadButton')}</div>
                      </div>
                    )}
                  </Upload>
                  {errors.ktaPdhiDocument && (
                    <p className="mt-1 text-xs text-red-500">{errors.ktaPdhiDocument}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.ktpLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Upload
                    listType="picture-card"
                    fileList={formData.ktpDocument}
                    onChange={({ fileList }) => handleInputChange('ktpDocument', fileList)}
                    beforeUpload={() => false}
                    maxCount={1}
                  >
                    {formData.ktpDocument.length === 0 && (
                      <div>
                        <div className="text-2xl">🪪</div>
                        <div className="mt-2 text-xs">{t('clinicSetup.uploadButton')}</div>
                      </div>
                    )}
                  </Upload>
                  {errors.ktpDocument && (
                    <p className="mt-1 text-xs text-red-500">{errors.ktpDocument}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Clinic Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">{t('clinicSetup.clinicInfoTitle')}</h2>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.clinicNameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder={t('clinicSetup.clinicNamePlaceholder')}
                    value={formData.clinicName}
                    onChange={(e) => handleInputChange('clinicName', e.target.value)}
                    status={errors.clinicName ? 'error' : ''}
                  />
                  {errors.clinicName && (
                    <p className="mt-1 text-xs text-red-500">{errors.clinicName}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.businessTypeLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Select
                    size="large"
                    className="w-full"
                    placeholder={t('clinicSetup.businessTypePlaceholder')}
                    value={formData.businessType || undefined}
                    onChange={(value) => handleInputChange('businessType', value)}
                    status={errors.businessType ? 'error' : ''}
                  >
                    <Option value="pt">PT (Perseroan Terbatas)</Option>
                    <Option value="cv">CV (Commanditaire Vennootschap)</Option>
                    <Option value="perseorangan">Perseorangan</Option>
                  </Select>
                  {errors.businessType && (
                    <p className="mt-1 text-xs text-red-500">{errors.businessType}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.phoneLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder={t('clinicSetup.phonePlaceholder')}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    status={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.emailLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    type="email"
                    placeholder={t('clinicSetup.emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    status={errors.email ? 'error' : ''}
                    disabled={true}
                    className="bg-gray-50"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t('clinicSetup.emailFromSession')}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.addressLabel')} <span className="text-red-500">*</span>
                  </label>
                  <TextArea
                    rows={3}
                    placeholder={t('clinicSetup.addressPlaceholder')}
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    status={errors.address ? 'error' : ''}
                  />
                  {errors.address && (
                    <p className="mt-1 text-xs text-red-500">{errors.address}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.cityLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder={t('clinicSetup.cityPlaceholder')}
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    status={errors.city ? 'error' : ''}
                  />
                  {errors.city && (
                    <p className="mt-1 text-xs text-red-500">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.provinceLabel')}
                  </label>
                  <Input
                    size="large"
                    placeholder={t('clinicSetup.provincePlaceholder')}
                    value={formData.province}
                    onChange={(e) => handleInputChange('province', e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('clinicSetup.postalCodeLabel')}
                  </label>
                  <Input
                    size="large"
                    placeholder={t('clinicSetup.postalCodePlaceholder')}
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Facilities */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">{t('clinicSetup.facilitiesTitle')}</h2>
              <p className="text-sm text-gray-600">{t('clinicSetup.facilitiesDescription')}</p>

              <div className="space-y-6">
                {/* Diagnostic */}
                <div>
                  <h3 className="mb-3 font-semibold text-gray-900">
                    {t('clinicSetup.facilities.diagnosticTitle')}
                  </h3>
                  <Checkbox.Group
                    options={diagnosticOptions}
                    value={formData.facilities.diagnostic}
                    onChange={(values) => handleFacilityChange('diagnostic', values as string[])}
                    className="flex flex-col gap-2"
                  />
                </div>

                {/* Laboratory */}
                <div>
                  <h3 className="mb-3 font-semibold text-gray-900">
                    {t('clinicSetup.facilities.laboratoryTitle')}
                  </h3>
                  <Checkbox.Group
                    options={laboratoryOptions}
                    value={formData.facilities.laboratory}
                    onChange={(values) => handleFacilityChange('laboratory', values as string[])}
                    className="flex flex-col gap-2"
                  />
                </div>

                {/* Hospitalization */}
                <div>
                  <h3 className="mb-3 font-semibold text-gray-900">
                    {t('clinicSetup.facilities.hospitalizationTitle')}
                  </h3>
                  <Checkbox.Group
                    options={hospitalizationOptions}
                    value={formData.facilities.hospitalization}
                    onChange={(values) => handleFacilityChange('hospitalization', values as string[])}
                    className="flex flex-col gap-2"
                  />
                </div>

                {/* Surgery */}
                <div>
                  <h3 className="mb-3 font-semibold text-gray-900">
                    {t('clinicSetup.facilities.surgeryTitle')}
                  </h3>
                  <Checkbox.Group
                    options={surgeryOptions}
                    value={formData.facilities.surgery}
                    onChange={(values) => handleFacilityChange('surgery', values as string[])}
                    className="flex flex-col gap-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between border-t border-gray-200 pt-6">
            <Button
              variant="default"
              size="large"
              onClick={handleBack}
              disabled={currentStep === 1 || isLoading}
            >
              {t('clinicSetup.backButton')}
            </Button>

            {currentStep < 3 ? (
              <Button
                variant="primary"
                size="large"
                onClick={handleNext}
              >
                {t('clinicSetup.nextButton')}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="large"
                onClick={handleSubmit}
                isLoading={isLoading}
              >
                {t('clinicSetup.submitButton')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicSetup;
